import React, { forwardRef, useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography,
  IconButton, Tooltip, CircularProgress, Alert, Tabs, Tab, TextField, Button,
  Paper, Stack, FormControl, Snackbar,
  FormControlLabel, Switch
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useMediaQuery, Autocomplete, Chip, Avatar, Slide, ToggleButtonGroup, ToggleButton } from '@mui/material';
import SimpleSuccessSpinner from '../LoadingSpinner';
import { useModalSolicitudesController, documentos } from './hooks/useModalSolicitudesController';
import { SectionCard, elegantInputSx, bytesToSize } from './components/SectionCard';
import { DocumentosCheckboxes } from './components/DocumentosCheckboxes';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FacturasAutocomplete = React.memo(({ loadingFacturas, facturaOptionsRemote, facturasSeleccionadas, handleFacturasChange, error }) => {
  return (
    <Autocomplete
      multiple loading={loadingFacturas} options={facturaOptionsRemote}
      value={(facturasSeleccionadas || []).map((f) => ({ ...f }))}
      onChange={handleFacturasChange}
      getOptionDisabled={(opt) => Boolean(opt.cancelada)}
      getOptionLabel={(opt) => `${opt.identificador || ''}${opt.fecha ? ` • ${opt.fecha}` : ''}${opt.cancelada ? ' • CANCELADA' : ''}`}
      isOptionEqualToValue={(a, b) => (a?.uuid || '') === (b?.uuid || '')}
      renderTags={(value, getTagProps) => value.map((option, index) => {
        const label = option.identificador || '';
        return (
          <Tooltip key={option.uuid || `${label}-${index}`} title={label}>
            <Chip {...getTagProps({ index })} label={label} size="small" />
          </Tooltip>
        );
      })}
      renderInput={(params) => (
        <TextField {...params} label="Seleccionar factura(s)" placeholder="Ej. 21473, 21474..." error={Boolean(error)} helperText={error || ' '} sx={elegantInputSx} />
      )}
    />
  );
});

const STABLE_EMPTY_ARRAY = [];

const SummaryCell = ({ label, value, wide = false }) => {
  const text = value == null || value === '' ? '-' : String(value);
  return (
    <Box sx={{ minWidth: 0, gridColumn: wide ? { xs: 'span 2', sm: 'span 2' } : 'auto' }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'rgba(255,255,255,0.72)',
          fontWeight: 800,
          fontSize: 'clamp(0.52rem, 0.5rem + 0.06vw, 0.60rem)',
          lineHeight: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.04em'
        }}
      >
        {label}
      </Typography>
      <Tooltip title={text} arrow>
        <Typography
          variant="body2"
          sx={{
            mt: 0.2,
            color: '#ffffff',
            fontWeight: 800,
            fontSize: 'clamp(0.72rem, 0.7rem + 0.1vw, 0.82rem)',
            lineHeight: 1.1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {text}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const formatEmpresaCorta = (value = '') => {
  const text = String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  if (text.includes('OFFSHORE')) return 'OFFSHORE';
  if (text.includes('PESADO')) return 'PESADO';
  if (text.includes('CONSTRU')) return 'CONSTRUCCIONES';
  return value || '-';
};

// Helper for Facturas selection inside the main component
const ModalSolicitudes = ({ 
  open, 
  onClose, 
  rowData, 
  onSaved, 
  initialFiles = STABLE_EMPTY_ARRAY, 
  isResend = false,
  guidedMode = false,
  correctionMetadata = null,
  reclassificationOnly = false
}) => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));

  const {
    formData, setFormData, combinedErrors, setCombinedErrors, totalOCError,
    maxTotalOC, formattedTotalOC, setFormattedTotalOC, setTotalOCError,
    loading, spinnerMessage, updatingOC, numeroEconomicoBloqueado,
    facturaOptionsRemote, loadingFacturas, ocEnCero, snackbar, setSnackbar,
    condicionCreditoRaw, monedaTipo, requiereFacturaAntesPago,
    configNotificarAutorizador,
    handleActualizarOC, handleConfirmGuardar, handleDialogClose, handleTogglePreferenciaProveedor,
    formatCurrency, parseCurrency, handleFileChange, handleDrop, handleRemoveFile, handleUpdateFileType, handleCheckboxChange
  } = useModalSolicitudesController(open, onClose, rowData, onSaved, initialFiles, isResend, guidedMode, correctionMetadata, reclassificationOnly);

  const [isFullScreen] = useState(false);
  const [tab, setTab] = useState(0);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [notasOpen, setNotasOpen] = useState(false);
  const useStepFlow = mdDown || reclassificationOnly;

  // Reset tab position every time the modal opens
  const prevOpen = React.useRef(false);
  React.useEffect(() => {
    if (open && !prevOpen.current) setTab(0);
    prevOpen.current = open;
  }, [open]);

  const totalOCRef = useRef(null);
  const refSolicitud = useRef(null);
  const refDescripcion = useRef(null);
  const refDocs = useRef(null);
  const refFacturas = useRef(null);

  const today = new Date().toISOString().split('T')[0];
  const mostrarFacturas = requiereFacturaAntesPago || facturaOptionsRemote.length > 0;
  const stepIndexes = {
    solicitud: !reclassificationOnly ? 0 : -1,
    facturas: mostrarFacturas ? (reclassificationOnly ? 0 : 1) : -1,
    descripcion: !reclassificationOnly ? (mostrarFacturas ? 2 : 1) : -1,
    docs: reclassificationOnly ? (mostrarFacturas ? 1 : 0) : (mostrarFacturas ? 3 : 2),
  };
  const safeTab = Math.min(tab, stepIndexes.docs);
  const visibleSteps = [
    !reclassificationOnly && { key: 'solicitud', label: 'Solicitud', index: stepIndexes.solicitud },
    mostrarFacturas && { key: 'facturas', label: 'Comp. factura', index: stepIndexes.facturas },
    !reclassificationOnly && { key: 'descripcion', label: 'Descripcion', index: stepIndexes.descripcion },
    { key: 'docs', label: 'Adjuntos', index: stepIndexes.docs },
  ].filter(Boolean);
  const currentStepPosition = Math.max(0, visibleSteps.findIndex(step => step.index === safeTab));
  const currentStep = visibleSteps[currentStepPosition] || visibleSteps[0];
  const isLastStep = currentStepPosition >= visibleSteps.length - 1;
  const goToStep = useCallback((direction) => {
    const nextStep = visibleSteps[currentStepPosition + direction];
    if (nextStep) setTab(nextStep.index);
  }, [currentStepPosition, visibleSteps]);
  const montoResumen = formattedTotalOC || formData.TOTALOC || formData.TOTAL || formData.TOTAL_OC || '-';
  const proveedorResumen = formData.PROVEEDOR || formData.proveedorNombre || rowData?.PROVEEDOR || '-';
  const ccNum = rowData?.NUMEROCC || rowData?.NUMERO_CC || formData.NUMEROCC || formData.NUMERO_CC || '';
  const ccNom = rowData?.NOMBRE_CC || formData.NOMBRE_CC || formData.CENTRO_COSTOS || rowData?.CENTRO_COSTOS || formData.CENTROCOSTOS || rowData?.CENTROCOSTOS || rowData?.CENTROCOSTO || '';
  const centroResumen = (ccNum && ccNom && ccNum !== ccNom) 
    ? `${ccNum} - ${ccNom}` 
    : (ccNom || ccNum || '-');
  const empresaResumen = formatEmpresaCorta(formData.EMPRESA || rowData?.EMPRESA || '-');
  const folioResumen = formData.FOLIO || rowData?.FOLIO || rowData?.FOLIO_ORDENCOMPRA || '-';
  const divisaResumen = formData.DIVISA || rowData?.DIVISA || '';
  const notasRequi = (
    formData.NOTAS_REQUI || formData.NOTAS || formData.notasRequi ||
    rowData?.NOTAS_REQUI || rowData?.NOTAS || rowData?.notasRequi || ''
  ).toString().trim();
  const notasRequiLarga = notasRequi.length > 140;
  const documentosVisibles = React.useMemo(() => {
    return documentos.filter(({ name }) => {
      if (name === 'cartaBancariamx' || name === 'estadoCuentamx') return monedaTipo === 'MXN';
      if (name === 'cartaBancariaus' || name === 'estadoCuentaus') return monedaTipo === 'USD';
      return true;
    });
  }, [monedaTipo]);
  const documentosMarcados = documentosVisibles.filter(({ name }) => Boolean(formData.documentos?.[name])).length;
  React.useEffect(() => {
    if (combinedErrors.documentos) setDocsDialogOpen(true);
  }, [combinedErrors.documentos]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'TOTALOC') {
      const validChars = /^[0-9.,$]*$/;
      if (!validChars.test(value)) return;
      setFormattedTotalOC(value);
      const parsedValue = parseCurrency(value);
      const num = parseFloat(parsedValue);
      setFormData((prev) => ({ ...prev, [name]: parsedValue }));
      setCombinedErrors((prev) => {
        const updated = { ...prev };
        if (!isNaN(num) && num <= maxTotalOC && num >= 0) delete updated.TOTALOC;
        return updated;
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setCombinedErrors((prev) => {
        const updated = { ...prev };
        if (name === 'fechaVencimientoPrecio' && value) delete updated.fechaVencimientoPrecio;
        return updated;
      });
    }
  }, [maxTotalOC, parseCurrency, setCombinedErrors, setFormData, setFormattedTotalOC]);

  const handleTotalOCOnBlur = useCallback(() => {
    const raw = totalOCRef.current?.value || '';
    const parsed = parseCurrency(raw);
    const num = parseFloat(parsed);

    if (!parsed || isNaN(num)) {
      setTotalOCError('Monto inválido');
      setFormattedTotalOC(raw);
      return;
    }
    if (num <= 0) {
      setTotalOCError('El monto debe ser mayor a 0');
      setFormattedTotalOC(formatCurrency(num));
      return;
    }
    if (num > maxTotalOC) {
      setTotalOCError(`No puedes poner una cantidad mayor a ${formatCurrency(maxTotalOC)}`);
      setFormattedTotalOC(formatCurrency(num));
      return;
    }

    setTotalOCError('');
    setFormattedTotalOC(formatCurrency(num));
    setFormData((prev) => ({ ...prev, TOTALOC: num.toString() }));
  }, [maxTotalOC, parseCurrency, formatCurrency, setFormData, setFormattedTotalOC, setTotalOCError]);



  const handleFacturasChange = useCallback((_, selected) => {
    const selectedSafe = Array.isArray(selected) ? selected : [];
    setFormData((prev) => {
      const mapPrev = new Map((prev.facturasSeleccionadas || []).map(x => [x.uuid, x]));
      const next = selectedSafe.map((opt) => {
        const prevItem = mapPrev.get(opt.uuid);
        return { ...opt, pdfFile: prevItem?.pdfFile || null, xmlFile: prevItem?.xmlFile || null };
      });
      return { ...prev, facturasSeleccionadas: next };
    });
    setCombinedErrors((prev) => {
      const u = { ...prev };
      delete u.facturasSeleccionadas;
      return u;
    });
  }, [setFormData, setCombinedErrors]);

  const setFacturaFileByUuid = useCallback((uuid, type, file) => {
    setFormData((prev) => {
      const next = (prev.facturasSeleccionadas || []).map((f) => {
        if (f.uuid !== uuid) return f;
        return { ...f, [type]: file };
      });
      return { ...prev, facturasSeleccionadas: next };
    });
    setCombinedErrors((prev) => {
      const u = { ...prev };
      delete u[`${type === 'pdfFile' ? 'facturaPdf' : 'facturaXml'}__${uuid}`];
      return u;
    });
  }, [setFormData, setCombinedErrors]);

  const onFacturaPdfPick = useCallback((uuid) => (e) => {
    const file = e.target.files?.[0] || null;
    if (file) setFacturaFileByUuid(uuid, 'pdfFile', file);
    e.target.value = '';
  }, [setFacturaFileByUuid]);

  const onFacturaXmlPick = useCallback((uuid) => (e) => {
    const file = e.target.files?.[0] || null;
    if (file) setFacturaFileByUuid(uuid, 'xmlFile', file);
    e.target.value = '';
  }, [setFacturaFileByUuid]);

  const handleDropFacturaFile = useCallback((uuid, type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0] || null;
    if (!file) return;
    setFacturaFileByUuid(uuid, type, file);
  }, [setFacturaFileByUuid]);

  return (
    <Dialog
      open={open}
      disableRestoreFocus
      onClose={handleDialogClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isFullScreen || smDown}
      TransitionComponent={Transition}
      keepMounted={false}
      PaperProps={{
        sx: {
          borderRadius: (isFullScreen || smDown) ? 0 : '16px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
          backgroundColor: '#f8fafc',
          maxHeight: 'clamp(480px, 90vh, 880px)',
          maxWidth: 'clamp(580px, 86vw, 1100px) !important',
        }
      }}
    >
      <Box
        sx={{
          p: 'clamp(8px, 1vw, 12px)',
          background: 'linear-gradient(135deg, #0655A5 0%, #033C75 100%)',
          color: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.8vw, 10px)' }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', width: 'clamp(28px, 2vw, 32px)', height: 'clamp(28px, 2vw, 32px)', borderRadius: '10px' }}>
            <PaymentRoundedIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 19px)' }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: '0.65fr 0.95fr 0.8fr 1.1fr',
                },
                columnGap: 'clamp(8px, 1vw, 14px)',
                rowGap: 'clamp(4px, 0.6vw, 8px)',
                px: 'clamp(8px, 1vw, 12px)',
                py: 'clamp(6px, 0.7vw, 9px)',
                borderRadius: '12px',
                bgcolor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.18)',
              }}
            >
              <SummaryCell label="OC" value={folioResumen} />
              <SummaryCell label="Monto" value={`${montoResumen}${divisaResumen ? ` ${divisaResumen}` : ''}`} />
              <SummaryCell label="Empresa" value={empresaResumen} />
              {ccNum && <SummaryCell label="No. CC" value={ccNum} />}
              {ccNom && <SummaryCell label="Centro de costo" value={ccNom} />}
              <SummaryCell label="Proveedor" value={proveedorResumen} wide />
              {condicionCreditoRaw && <SummaryCell label="Condición pago" value={condicionCreditoRaw} wide />}
            </Box>
            {guidedMode && correctionMetadata?.motivo && (
              <Box sx={{ 
                mt: 1, 
                p: 1, 
                bgcolor: 'rgba(255, 152, 0, 0.15)', 
                borderRadius: 1.5, 
                borderLeft: '4px solid #FF9800',
                display: 'inline-block'
              }}>
                <Typography variant="caption" sx={{ color: '#FFD180', fontWeight: 900, display: 'block', mb: 0.25, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Motivo del rechazo:
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontStyle: 'italic', lineHeight: 1.2 }}>
                  "{correctionMetadata.motivo}"
                </Typography>
              </Box>
            )}
            {reclassificationOnly && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Chip 
                  label="ORDEN DE COMPRA" 
                  size="small" 
                  sx={{ 
                    bgcolor: (formData.adjuntos || []).some(a => ['OC', 'ORDEN DE COMPRA', 'ORDENES DE COMPRA'].includes(a.type?.toUpperCase())) ? '#E8F5E9' : '#FFEBEE',
                    color: (formData.adjuntos || []).some(a => ['OC', 'ORDEN DE COMPRA', 'ORDENES DE COMPRA'].includes(a.type?.toUpperCase())) ? '#2E7D32' : '#C62828',
                    fontWeight: 700, fontSize: '0.65rem', border: '1px solid',
                    borderColor: (formData.adjuntos || []).some(a => ['OC', 'ORDEN DE COMPRA', 'ORDENES DE COMPRA'].includes(a.type?.toUpperCase())) ? '#C8E6C9' : '#FFCDD2'
                  }} 
                />
                <Chip 
                  label="COMPLEMENTO FACTURA" 
                  size="small" 
                  sx={{ 
                    bgcolor: (formData.adjuntos || []).some(a => ['FACTURA', 'FACTURAS', 'FAC'].includes(a.type?.toUpperCase())) ? '#E8F5E9' : '#FFEBEE',
                    color: (formData.adjuntos || []).some(a => ['FACTURA', 'FACTURAS', 'FAC'].includes(a.type?.toUpperCase())) ? '#2E7D32' : '#C62828',
                    fontWeight: 700, fontSize: '0.65rem', border: '1px solid',
                    borderColor: (formData.adjuntos || []).some(a => ['FACTURA', 'FACTURAS', 'FAC'].includes(a.type?.toUpperCase())) ? '#C8E6C9' : '#FFCDD2'
                  }} 
                />
                <Chip 
                  label="DATOS BANCARIOS" 
                  size="small" 
                  sx={{ 
                    bgcolor: (formData.adjuntos || []).some(a => ['CARATULA', 'DATOS BANCARIOS', 'CARATULAS', 'CB'].includes(a.type?.toUpperCase())) ? '#E8F5E9' : '#FFEBEE',
                    color: (formData.adjuntos || []).some(a => ['CARATULA', 'DATOS BANCARIOS', 'CARATULAS', 'CB'].includes(a.type?.toUpperCase())) ? '#2E7D32' : '#C62828',
                    fontWeight: 700, fontSize: '0.65rem', border: '1px solid',
                    borderColor: (formData.adjuntos || []).some(a => ['CARATULA', 'DATOS BANCARIOS', 'CARATULAS', 'CB'].includes(a.type?.toUpperCase())) ? '#C8E6C9' : '#FFCDD2'
                  }} 
                />
              </Stack>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Actualizar OC desde ERP">
              <span>
                <IconButton onClick={handleActualizarOC} disabled={updatingOC} sx={{ color: 'white' }}>
                  {updatingOC ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <SyncRoundedIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cerrar">
              <IconButton edge="end" onClick={handleDialogClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {ocEnCero && !reclassificationOnly && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Esta O.C. viene en <b>$0</b> desde ERP. Presiona el ícono <b>Actualizar</b> (⟳) para traer el monto real y poder guardar la solicitud.
          </Alert>
        )}

        {useStepFlow && (
          <Tabs
            value={safeTab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mt: 1.5,
              minHeight: 38,
              backgroundColor: 'rgba(0,0,0,0.15)',
              borderRadius: 2,
              p: 0.5,
              '& .MuiTabs-indicator': {
                backgroundColor: 'white',
                borderRadius: 1.5,
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              },
              '& .MuiTab-root': {
                zIndex: 1,
                minHeight: 32,
                py: 0.5,
                px: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.7)',
                transition: 'color 0.2s',
                '&.Mui-selected': { color: '#033C75' }
              }
            }}
          >
            {!reclassificationOnly && <Tab label="1. Solicitud" onClick={() => setTab(stepIndexes.solicitud)} disableRipple />}
            {mostrarFacturas && <Tab label={reclassificationOnly ? "1. Factura" : "2. Factura"} onClick={() => setTab(stepIndexes.facturas)} disableRipple />}
            {!reclassificationOnly && <Tab label={mostrarFacturas ? "3. Concepto" : "2. Concepto"} onClick={() => setTab(stepIndexes.descripcion)} disableRipple />}
            <Tab label={reclassificationOnly ? (mostrarFacturas ? "2. Adjuntos" : "1. Adjuntos") : (mostrarFacturas ? "4. Adjuntos" : "3. Adjuntos")} onClick={() => setTab(stepIndexes.docs)} disableRipple />}
          </Tabs>
        )}
      </Box>

      <DialogContent
        dividers
        sx={{
          p: 'clamp(8px, 1.2vw, 16px)',
          minHeight: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: useStepFlow ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 'clamp(8px, 1vw, 14px)',
          alignItems: 'start',
          bgcolor: '#f8fafc',
        }}
      >
        {notasRequi && (
          <Paper
            elevation={0}
            sx={{
              gridColumn: '1 / -1',
              px: 'clamp(8px, 1vw, 12px)',
              py: 'clamp(4px, 0.5vw, 6px)',
              borderRadius: '10px',
              bgcolor: '#f0f7ff',
              border: '1px solid #bae6fd',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr) auto' },
              alignItems: 'center',
              gap: { xs: 0.4, sm: 1 },
            }}
          >
            <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.72rem)', fontWeight: 900, color: '#0284c7', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              Notas requi
            </Typography>
            <Typography
              sx={{
                color: '#0f172a',
                fontWeight: 700,
                fontSize: 'clamp(0.68rem, 0.66rem + 0.06vw, 0.76rem)',
                lineHeight: 1.25,
                overflow: 'hidden',
                display: notasOpen ? 'block' : '-webkit-box',
                WebkitLineClamp: notasOpen ? 'initial' : 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {notasRequi}
            </Typography>
            {notasRequiLarga && (
              <Button size="small" onClick={() => setNotasOpen((prev) => !prev)} sx={{ borderRadius: 8, fontWeight: 800, textTransform: 'none', fontSize: '0.68rem', py: 0.1, justifySelf: { xs: 'start', sm: 'end' } }}>
                {notasOpen ? 'Ver menos' : 'Ver nota'}
              </Button>
            )}
          </Paper>
        )}
        {useStepFlow && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, gridColumn: '1 / -1' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
            Paso {currentStepPosition + 1} de {visibleSteps.length}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#0655A5' }}>
            {currentStep?.label}
          </Typography>
        </Box>
        )}
        {!reclassificationOnly && (!useStepFlow || safeTab === stepIndexes.solicitud) && (
          <>
            {/* --- SOLICITUD --- */}
            <div ref={refSolicitud} style={{ display: 'none' }} />
            <SectionCard
              icon={<DescriptionRoundedIcon />}
              title="Datos de la solicitud"
              subtitle="Monto y fechas"
              error={totalOCError || combinedErrors.TOTALOC || combinedErrors.fechaVencimientoPrecio || combinedErrors.numeroEconomico}
              sx={{ mb: useStepFlow ? 1.5 : 0, gridColumn: { md: useStepFlow ? 'auto' : '1' } }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 'clamp(6px, 0.8vw, 10px)' }}>
                <FormControlLabel
                  control={
                    <span>
                      <Switch
                        checked={requiereFacturaAntesPago}
                        onChange={handleTogglePreferenciaProveedor}
                        size="small"
                        color="primary"
                        disabled={facturaOptionsRemote.length > 0}
                      />
                    </span>
                  }
                  label={
                    <Tooltip title={facturaOptionsRemote.length > 0 ? "Ya existen facturas en el ERP, no es necesario cambiar la preferencia." : "Activa/Desactiva el requerimiento de factura"}>
                      <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', color: requiereFacturaAntesPago ? '#0655A5' : '#64748b', opacity: facturaOptionsRemote.length > 0 ? 0.6 : 1 }}>
                        {requiereFacturaAntesPago ? 'Requiere factura' : 'No requiere factura'}
                      </Typography>
                    </Tooltip>
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
                <FormControlLabel
                  control={<Switch checked={configNotificarAutorizador} disabled size="small" color="primary" />}
                  label={
                    <Tooltip title="Indica si actualmente el sistema esta configurado en el servidor para enviar correos de notificacion al primer autorizador.">
                      <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', color: configNotificarAutorizador ? '#0655A5' : '#64748b' }}>
                        {configNotificarAutorizador ? 'Notifica autorizador' : 'Notificar inactivo'}
                      </Typography>
                    </Tooltip>
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'clamp(6px, 0.8vw, 10px)' }}>
                <TextField
                  name="TOTALOC" label="Monto solicitado" type="text"
                  value={formattedTotalOC ?? ''}
                  error={Boolean(totalOCError) || Boolean(combinedErrors.TOTALOC)}
                  helperText={totalOCError || combinedErrors.TOTALOC || ''}
                  inputRef={totalOCRef} onChange={handleInputChange}
                  onFocus={(e) => e.target.select()} onBlur={handleTotalOCOnBlur}
                  fullWidth sx={{
                    ...elegantInputSx,
                    '& .MuiOutlinedInput-root': { ...elegantInputSx['& .MuiOutlinedInput-root'], minHeight: 'clamp(36px, 4.5vh, 42px)' },
                    '& .MuiInputBase-input': { py: 'clamp(5px, 0.8vh, 8px)' },
                  }}
                />
                <TextField
                  name="numeroEconomico" label="Numero economico"
                  value={formData.numeroEconomico ?? ''} onChange={handleInputChange} fullWidth
                  disabled={numeroEconomicoBloqueado}
                  InputLabelProps={numeroEconomicoBloqueado ? { shrink: true } : undefined}
                  helperText={numeroEconomicoBloqueado ? 'Viene bloqueado desde ERP' : ''}
                  sx={{
                    ...elegantInputSx,
                    '& .MuiOutlinedInput-root': { ...elegantInputSx['& .MuiOutlinedInput-root'], minHeight: 'clamp(36px, 4.5vh, 42px)' },
                    '& .MuiInputBase-input': { py: 'clamp(5px, 0.8vh, 8px)' },
                  }}
                />

                {/* Fila Simétrica: Tipo de Pago | Fecha Compromiso (si es Crédito) */}
                <Box sx={{ display: 'grid', gridTemplateColumns: formData.tipoPago === 'CREDITO' ? { xs: '1fr', sm: '1fr 1fr' } : '1fr', gap: 1, alignItems: 'start' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', color: combinedErrors.tipoPago ? 'error.main' : '#64748b', display: 'block', mb: 0.3 }}>
                      Tipo de pago *
                    </Typography>
                    <ToggleButtonGroup
                      value={formData.tipoPago || ''}
                      exclusive
                      onChange={(_, val) => val && setFormData(prev => ({ ...prev, tipoPago: val }))}
                      size="small"
                      fullWidth
                      sx={{
                        height: 'clamp(34px, 4.2vh, 40px)',
                        '& .MuiToggleButton-root': {
                          fontWeight: 800,
                          fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)',
                          textTransform: 'none',
                          borderColor: combinedErrors.tipoPago ? '#ef4444' : '#cbd5e1',
                          color: '#64748b',
                          borderRadius: '8px',
                          '&.Mui-selected': {
                            bgcolor: formData.tipoPago === 'CONTADO' ? '#16a34a' : '#0284c7',
                            color: '#ffffff',
                            borderColor: formData.tipoPago === 'CONTADO' ? '#16a34a' : '#0284c7',
                            boxShadow: '0 3px 10px rgba(15, 23, 42, 0.12)',
                            '&:hover': {
                              bgcolor: formData.tipoPago === 'CONTADO' ? '#15803d' : '#0369a1',
                            }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="CONTADO">
                        Contado
                      </ToggleButton>
                      <ToggleButton value="CREDITO">
                        Crédito
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {combinedErrors.tipoPago && (
                      <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.68rem', mt: 0.2, display: 'block' }}>
                        {combinedErrors.tipoPago}
                      </Typography>
                    )}
                  </Box>

                  {formData.tipoPago === 'CREDITO' && (
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', color: combinedErrors.fechaCompromiso ? 'error.main' : '#64748b', display: 'block', mb: 0.3 }}>
                        Fecha compromiso *
                      </Typography>
                      <TextField
                        name="fechaCompromiso"
                        type="date"
                        value={formData.fechaCompromiso ?? ''}
                        onChange={handleInputChange}
                        fullWidth
                        error={Boolean(combinedErrors.fechaCompromiso)}
                        helperText={combinedErrors.fechaCompromiso || ''}
                        inputProps={{ min: today }}
                        sx={{
                          ...elegantInputSx,
                          '& .MuiOutlinedInput-root': { ...elegantInputSx['& .MuiOutlinedInput-root'], minHeight: 'clamp(34px, 4.2vh, 40px)' },
                          '& .MuiInputBase-input': { py: 'clamp(5px, 0.8vh, 8px)' },
                        }}
                      />
                    </Box>
                  )}
                </Box>
                {(!requiereFacturaAntesPago && formData.facturasSeleccionadas?.length === 0) && (
                  <TextField
                    name="fechaVencimientoPrecio" label="Fecha de vencimiento (precio)" type="date"
                    value={formData.fechaVencimientoPrecio ?? ''} onChange={handleInputChange} fullWidth
                    error={Boolean(combinedErrors.fechaVencimientoPrecio)}
                    helperText={combinedErrors.fechaVencimientoPrecio || ''}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    sx={{
                      ...elegantInputSx,
                      '& .MuiOutlinedInput-root': { ...elegantInputSx['& .MuiOutlinedInput-root'], minHeight: 'clamp(36px, 4.5vh, 42px)' },
                      '& .MuiInputBase-input': { py: 'clamp(5px, 0.8vh, 8px)' },
                    }}
                  />
                )}
              </Box>
            </SectionCard>
          </>
        )}

                {/* --- FACTURAS --- */}
                {mostrarFacturas && (!useStepFlow || safeTab === stepIndexes.facturas) && (
                  <>
                    <div ref={refFacturas} style={{ display: 'none' }} />
                    <SectionCard 
                      icon={<DescriptionRoundedIcon />} 
                      title={requiereFacturaAntesPago ? "Comp. Factura requeridas" : "Comp. Factura (Opcional)"} 
                      subtitle={requiereFacturaAntesPago ? "Selecciona 1 o más y adjunta PDF/XML por cada una" : "Puedes seleccionar y adjuntar facturas si lo deseas"} 
                      error={combinedErrors.facturasSeleccionadas} 
                      sx={{ mb: useStepFlow ? 2 : 0, gridColumn: { md: useStepFlow ? 'auto' : '2' } }}
                    >
                  <FacturasAutocomplete
                    loadingFacturas={loadingFacturas}
                    facturaOptionsRemote={facturaOptionsRemote}
                    facturasSeleccionadas={formData.facturasSeleccionadas}
                    handleFacturasChange={handleFacturasChange}
                    error={combinedErrors.facturasSeleccionadas}
                  />

                  {(formData.facturasSeleccionadas || []).length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
                      {formData.facturasSeleccionadas.map((f) => (
                        <Paper key={f.uuid} elevation={0} sx={{ p: 'clamp(8px, 1vw, 12px)', borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', minWidth: 0 }}>
                          {/* Fila compacta: nombre+fecha | botones PDF y XML */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', minWidth: 0 }}>
                            {/* Identificador y fecha */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography noWrap sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.78rem, 0.74rem + 0.1vw, 0.86rem)' }}>
                                {f.identificador || 'Factura'}
                              </Typography>
                              <Typography sx={{ color: '#64748b', fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', fontWeight: 600 }}>
                                {f.fecha || 'Sin fecha'}
                              </Typography>
                            </Box>

                            {/* Botón PDF */}
                            <Box
                              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#0655A5'; e.currentTarget.style.background = '#eff6ff'; }}
                              onDragLeave={(e) => { e.currentTarget.style.borderColor = f.pdfFile ? '#16a34a' : '#cbd5e1'; e.currentTarget.style.background = f.pdfFile ? '#f0fdf4' : 'transparent'; }}
                              onDrop={(e) => { e.currentTarget.style.borderColor = f.pdfFile ? '#16a34a' : '#cbd5e1'; e.currentTarget.style.background = f.pdfFile ? '#f0fdf4' : 'transparent'; handleDropFacturaFile(f.uuid, 'pdfFile')(e); }}
                              sx={{
                                flexShrink: 0,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
                                p: 0.6,
                                borderRadius: '10px',
                                border: '1.5px dashed',
                                borderColor: f.pdfFile ? '#16a34a' : '#cbd5e1',
                                bgcolor: f.pdfFile ? '#f0fdf4' : '#ffffff',
                                transition: 'all 0.15s ease',
                                minWidth: 100,
                              }}
                            >
                              <input type="file" id={`facturaPdf__${f.uuid}`} accept=".pdf" style={{ display: 'none' }} onChange={onFacturaPdfPick(f.uuid)} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <label htmlFor={`facturaPdf__${f.uuid}`} style={{ cursor: 'pointer' }}>
                                  <Button
                                    size="small"
                                    variant={f.pdfFile ? 'contained' : 'outlined'}
                                    color={f.pdfFile ? 'success' : 'primary'}
                                    component="span"
                                    startIcon={<AttachFileIcon />}
                                    sx={{ fontWeight: 800, borderRadius: '8px', textTransform: 'none', fontSize: 'clamp(0.68rem, 0.66rem + 0.06vw, 0.74rem)', px: 1.2, py: 0.35, minWidth: 70 }}
                                  >
                                    PDF
                                  </Button>
                                </label>
                                {f.pdfFile && (
                                  <IconButton color="error" size="small" onClick={(e) => { e.stopPropagation(); setFacturaFileByUuid(f.uuid, 'pdfFile', null); }} sx={{ p: 0.3, border: '1px solid #fca5a5', borderRadius: 1 }}>
                                    <DeleteRoundedIcon sx={{ fontSize: '0.85rem' }} />
                                  </IconButton>
                                )}
                              </Box>
                              {f.pdfFile && (
                                <Typography noWrap title={f.pdfFile.name} sx={{ fontSize: '0.62rem', color: '#16a34a', maxWidth: 90, display: 'block', fontWeight: 600 }}>
                                  {f.pdfFile.name}
                                </Typography>
                              )}
                              {combinedErrors[`facturaPdf__${f.uuid}`] && <Typography variant="caption" color="error" sx={{ fontSize: '0.62rem' }}>{combinedErrors[`facturaPdf__${f.uuid}`]}</Typography>}
                            </Box>

                            {/* Botón XML */}
                            <Box
                              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#0655A5'; e.currentTarget.style.background = '#eff6ff'; }}
                              onDragLeave={(e) => { e.currentTarget.style.borderColor = f.xmlFile ? '#16a34a' : '#cbd5e1'; e.currentTarget.style.background = f.xmlFile ? '#f0fdf4' : 'transparent'; }}
                              onDrop={(e) => { e.currentTarget.style.borderColor = f.xmlFile ? '#16a34a' : '#cbd5e1'; e.currentTarget.style.background = f.xmlFile ? '#f0fdf4' : 'transparent'; handleDropFacturaFile(f.uuid, 'xmlFile')(e); }}
                              sx={{
                                flexShrink: 0,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
                                p: 0.6,
                                borderRadius: '10px',
                                border: '1.5px dashed',
                                borderColor: f.xmlFile ? '#16a34a' : '#cbd5e1',
                                bgcolor: f.xmlFile ? '#f0fdf4' : '#ffffff',
                                transition: 'all 0.15s ease',
                                minWidth: 100,
                              }}
                            >
                              <input type="file" id={`facturaXml__${f.uuid}`} accept=".xml" style={{ display: 'none' }} onChange={onFacturaXmlPick(f.uuid)} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <label htmlFor={`facturaXml__${f.uuid}`} style={{ cursor: 'pointer' }}>
                                  <Button
                                    size="small"
                                    variant={f.xmlFile ? 'contained' : 'outlined'}
                                    color={f.xmlFile ? 'success' : 'primary'}
                                    component="span"
                                    startIcon={<AttachFileIcon />}
                                    sx={{ fontWeight: 800, borderRadius: '8px', textTransform: 'none', fontSize: 'clamp(0.68rem, 0.66rem + 0.06vw, 0.74rem)', px: 1.2, py: 0.35, minWidth: 70 }}
                                  >
                                    XML
                                  </Button>
                                </label>
                                {f.xmlFile && (
                                  <IconButton color="error" size="small" onClick={(e) => { e.stopPropagation(); setFacturaFileByUuid(f.uuid, 'xmlFile', null); }} sx={{ p: 0.3, border: '1px solid #fca5a5', borderRadius: 1 }}>
                                    <DeleteRoundedIcon sx={{ fontSize: '0.85rem' }} />
                                  </IconButton>
                                )}
                              </Box>
                              {f.xmlFile && (
                                <Typography noWrap title={f.xmlFile.name} sx={{ fontSize: '0.62rem', color: '#16a34a', maxWidth: 90, display: 'block', fontWeight: 600 }}>
                                  {f.xmlFile.name}
                                </Typography>
                              )}
                              {combinedErrors[`facturaXml__${f.uuid}`] && <Typography variant="caption" color="error" sx={{ fontSize: '0.62rem' }}>{combinedErrors[`facturaXml__${f.uuid}`]}</Typography>}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                    </SectionCard>
                  </>
                )}

            {!reclassificationOnly && (!useStepFlow || safeTab === stepIndexes.descripcion) && (
              <>
                {/* --- DESCRIPCIÓN --- */}
                <div ref={refDescripcion} style={{ display: 'none' }} />
                <SectionCard icon={<DescriptionRoundedIcon />} title="Concepto y observaciones" subtitle="Qué se paga y notas relevantes" sx={{ mb: useStepFlow ? 2 : 0, gridColumn: { md: useStepFlow ? 'auto' : '1' } }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ flex: '1 1 100%' }}>
                      <TextField name="conceptoPago" label="Concepto de pago" value={formData.conceptoPago ?? ''} onChange={handleInputChange} fullWidth margin="dense" multiline rows={2} sx={elegantInputSx} />
                      <Typography sx={{ mt: 0.25, display: 'block', fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', color: '#64748b', fontWeight: 500 }}>Ej.: “OC 42367 • Renta de equipo • Enero 2026”</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 100%' }}>
                      <TextField name="observaciones" label="Observaciones" value={formData.observaciones ?? ''} onChange={handleInputChange} fullWidth margin="dense" multiline rows={2} sx={elegantInputSx} />
                      <Typography sx={{ mt: 0.25, display: 'block', fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', color: '#64748b', fontWeight: 500 }}>Notas internas / instrucciones para tesorería.</Typography>
                    </Box>
                  </Box>
                </SectionCard>
              </>
            )}

        {(!useStepFlow || safeTab === stepIndexes.docs) && (
        <>
        {/* --- ADJUNTOS --- */}
        <div ref={refDocs} style={{ display: 'none' }} />
        <SectionCard
          icon={<AttachFileIcon />}
          title="Adjuntos y documentación"
          subtitle="Carga archivos y etiqueta los importantes"
          error={combinedErrors.adjuntos}
          sx={{
            gridColumn: { md: useStepFlow ? 'auto' : '2' },
            gridRow: {
              md: !useStepFlow && !mostrarFacturas ? (notasRequi ? '2' : '1') : 'auto',
            },
          }}
        >
          <FormControl fullWidth>
            <Box
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: '2px dashed #93c5fd',
                py: 'clamp(14px, 2vw, 22px)',
                px: 'clamp(12px, 1.5vw, 18px)',
                textAlign: 'center',
                borderRadius: '14px',
                backgroundColor: 'rgba(239, 246, 255, 0.45)',
                minHeight: 90,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all .2s ease',
                '&:hover': {
                  borderColor: '#0655A5',
                  backgroundColor: 'rgba(219, 234, 254, 0.7)',
                }
              }}
            >
              <input type="file" id="adjunto" accept=".pdf,.jpg,.jpeg,.png,.xml,.zip,.xls,.xlsx,.doc,.docx" style={{ display: 'none' }} multiple onChange={handleFileChange} />
              <label htmlFor="adjunto" style={{ cursor: 'pointer' }}>
                <Button size="small" variant="outlined" component="span" startIcon={<AttachFileIcon />} sx={{ borderRadius: '10px', fontWeight: 800, px: 'clamp(14px, 1.5vw, 20px)', fontSize: 'clamp(0.72rem, 0.7rem + 0.08vw, 0.80rem)', borderColor: '#93c5fd', color: '#0655A5' }}>
                  SELECCIONAR ARCHIVOS
                </Button>
              </label>
              <Typography sx={{ display: 'block', mt: 0.6, color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)' }}>
                También puedes arrastrar y soltar aquí
              </Typography>
            </Box>
            {combinedErrors.adjuntos && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{combinedErrors.adjuntos}</Typography>}
            {combinedErrors.caratulaBancaria && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>• {combinedErrors.caratulaBancaria}</Typography>}
            {combinedErrors.ordenCompraAdjunto && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>• {combinedErrors.ordenCompraAdjunto}</Typography>}
            {formData.adjuntos.length > 0 && (
              <Stack spacing={0.75} sx={{ mt: 1.25, maxHeight: 150, overflowY: 'auto', pr: 0.5 }}>
                {formData.adjuntos.map((fileObj, idx) => {
                  return (
                  <Paper
                    key={`${fileObj.file.name}-${idx}`}
                    elevation={0}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) auto auto' },
                      alignItems: 'center',
                      gap: 0.75,
                      px: 'clamp(8px, 1vw, 12px)',
                      py: 'clamp(5px, 0.6vw, 8px)',
                      borderRadius: '10px',
                      bgcolor: fileObj.type !== '' ? '#eff6ff' : '#f8fafc',
                      border: '1px solid',
                      borderColor: fileObj.type !== '' ? '#bfdbfe' : '#e2e8f0',
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <AttachFileIcon fontSize="small" color={fileObj.type !== '' ? 'primary' : 'inherit'} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: fileObj.type !== '' ? 800 : 600, fontSize: 'clamp(0.72rem, 0.7rem + 0.08vw, 0.80rem)', color: '#0f172a' }} noWrap title={fileObj.file.name}>{fileObj.file.name}</Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.66rem', fontWeight: 500 }}>{fileObj.file?.serverSide ? 'Archivo original' : bytesToSize(fileObj.file?.size || 0)}</Typography>
                      </Box>
                    </Box>
                    <ToggleButtonGroup
                      value={fileObj.type}
                      exclusive
                      onChange={(e, newType) => handleUpdateFileType(idx, newType === null ? '' : newType)}
                      size="small"
                      sx={{
                        justifySelf: { xs: 'end', sm: 'auto' },
                        height: 28,
                        '& .MuiToggleButton-root': {
                          px: 1,
                          minWidth: 54,
                          fontSize: '0.66rem',
                          lineHeight: 1,
                          textTransform: 'none',
                          fontWeight: 800,
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: '#cbd5e1',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    >
                      <ToggleButton value="CARATULA" title="Caratula bancaria" sx={{ '&.Mui-selected': { bgcolor: '#16a34a !important', color: '#fff !important', borderColor: '#16a34a !important' } }}>Caratula</ToggleButton>
                      <ToggleButton value="OC" title="Orden de compra" sx={{ '&.Mui-selected': { bgcolor: '#ea580c !important', color: '#fff !important', borderColor: '#ea580c !important' } }}>OC</ToggleButton>
                      <ToggleButton value="FACTURA" title="Complemento factura" sx={{ '&.Mui-selected': { bgcolor: '#0284c7 !important', color: '#fff !important', borderColor: '#0284c7 !important' } }}>Factura</ToggleButton>
                    </ToggleButtonGroup>
                    <IconButton size="small" onClick={() => handleRemoveFile(idx)} sx={{ justifySelf: 'end' }}><DeleteRoundedIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
                  </Paper>
                );
                })}
              </Stack>
            )}
          </FormControl>
          <Paper
            elevation={0}
            sx={{
              mt: 1.25,
              px: 'clamp(10px, 1.2vw, 14px)',
              py: 'clamp(6px, 0.8vw, 10px)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              bgcolor: combinedErrors.documentos ? '#fff7ed' : '#f8fafc',
              border: '1px solid',
              borderColor: combinedErrors.documentos ? '#fdba74' : '#e2e8f0',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2, fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)' }}>
                Docs adjuntos: {documentosMarcados}/{documentosVisibles.length}
              </Typography>
              <Typography sx={{ color: combinedErrors.documentos ? '#dc2626' : '#64748b', fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.72rem)', fontWeight: 500 }} noWrap>
                {combinedErrors.documentos || 'Marca o revisa los documentos que acompanan la solicitud.'}
              </Typography>
            </Box>
            <Button
              size="small"
              variant={combinedErrors.documentos ? 'contained' : 'outlined'}
              color={combinedErrors.documentos ? 'warning' : 'primary'}
              onClick={() => setDocsDialogOpen(true)}
              sx={{ borderRadius: '8px', px: 'clamp(10px, 1.2vw, 14px)', fontWeight: 800, textTransform: 'none', flexShrink: 0, fontSize: 'clamp(0.70rem, 0.68rem + 0.06vw, 0.76rem)' }}
            >
              Adjuntos
            </Button>
          </Paper>
        </SectionCard>
        </>
        )}
      </DialogContent>

      <DialogActions sx={{ position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', py: 'clamp(8px, 1vw, 12px)', px: 'clamp(14px, 2vw, 24px)', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Button onClick={handleDialogClose} startIcon={<CloseIcon sx={{ fontSize: 'clamp(14px, 1vw, 17px)' }} />} sx={{ borderRadius: '10px', px: 'clamp(12px, 1.5vw, 18px)', color: '#64748b', fontWeight: 700, textTransform: 'none', fontSize: 'clamp(0.76rem, 0.72rem + 0.1vw, 0.84rem)', '&:hover': { backgroundColor: '#f1f5f9', color: '#0f172a' } }}>Cancelar</Button>
        <Stack direction="row" spacing={1}>
          {useStepFlow && (
            <Button
              onClick={() => goToStep(-1)}
              disabled={currentStepPosition === 0 || loading}
              sx={{ borderRadius: '10px', px: 2, color: '#64748b', fontWeight: 700, textTransform: 'none' }}
            >
              Atras
            </Button>
          )}
          {useStepFlow && !isLastStep && (
            <Button
              onClick={() => goToStep(1)}
              variant="outlined"
              sx={{ borderRadius: '10px', px: 2.5, fontWeight: 800, textTransform: 'none' }}
            >
              Continuar
            </Button>
          )}
          <Button onClick={handleConfirmGuardar} disabled={loading || ocEnCero} variant="contained" startIcon={<SaveRoundedIcon sx={{ fontSize: 'clamp(15px, 1.1vw, 18px)' }} />} sx={{ borderRadius: '10px', px: 'clamp(16px, 2vw, 24px)', fontWeight: 800, textTransform: 'none', fontSize: 'clamp(0.78rem, 0.74rem + 0.1vw, 0.86rem)', background: 'linear-gradient(135deg, #0655A5 0%, #0284c7 100%)', boxShadow: '0 4px 14px rgba(6,85,165,0.3)', '&:hover': { boxShadow: '0 6px 20px rgba(6,85,165,0.45)' } }}>{loading ? 'Guardando...' : 'Guardar solicitud'}</Button>
        </Stack>
      </DialogActions>

      <Dialog
        open={docsDialogOpen && open}
        disableRestoreFocus
        onClose={() => setDocsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1.1 }}>
              Documentos incluidos
            </Typography>
            <Typography variant="caption" color={combinedErrors.documentos ? 'error' : 'text.secondary'}>
              {combinedErrors.documentos || `${documentosMarcados}/${documentosVisibles.length} marcados`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDocsDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 1.5, maxHeight: 360 }}>
          <DocumentosCheckboxes
            documentosState={formData.documentos}
            handleCheckboxChange={handleCheckboxChange}
            error={combinedErrors.documentos}
            monedaTipo={monedaTipo}
            requiereFacturaAntesPago={requiereFacturaAntesPago}
            documentos={documentos}
            lockedByFile={{
              caratulaBancaria: formData.adjuntos.some(a => a.type === 'CARATULA'),
              ordenCompra: formData.adjuntos.some(a => a.type === 'OC'),
              facturaa: (formData.facturasSeleccionadas || []).some(f => f.pdfFile),
              xml: (formData.facturasSeleccionadas || []).some(f => f.xmlFile),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="contained" onClick={() => setDocsDialogOpen(false)} sx={{ borderRadius: 8, px: 2, fontWeight: 800 }}>
            Listo
          </Button>
        </DialogActions>
      </Dialog>

      <SimpleSuccessSpinner open={loading} handleClose={() => null} message={spinnerMessage} />
      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.msg}</Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ModalSolicitudes;


